import React, { useState, useEffect } from 'react';
import "../Styles/login.css";
import { FaUserShield } from "react-icons/fa";
import { BsFillShieldLockFill } from "react-icons/bs";
import { AiOutlineSwapRight } from "react-icons/ai";
import logo from "../assets/claricelogo.png";
import { auth } from "../firebase";
import { signInWithCustomToken } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../apiConfig";
import Toast from "./Common/Toast"; 
import { getDeviceFingerprint } from "../services/deviceFingerprint";
import { getUserDevice, saveUserDevice } from "../services/firebaseService";
import { setupAuthPersistence } from '../services/authService';

const Login = () => {
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const navigate = useNavigate();

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const handleDeviceCheck = async (userId, userRole) => {
    
    if (userRole !== 'user') {
      return true;
    }
    
    try {
      console.log('🔐 Login: Getting current device fingerprint for normal user...');
      const currentDeviceId = await getDeviceFingerprint();
      
      if (!currentDeviceId) {
       
        throw new Error('Could not identify your device. Please try refreshing the page.');
      }

     
      const userDevice = await getUserDevice(userId);

      if (!userDevice) {
        
        
        const saveResult = await saveUserDevice(userId, currentDeviceId);
       
        
        if (saveResult) {
         
          showNotification('success', 'Welcome! Your device has been registered for security.');
          return true;
        } else {
          throw new Error('Failed to register your device. Please try again.');
        }
        
      } else if (!userDevice.deviceId || userDevice.deviceId === null) {
        
        
        const saveResult = await saveUserDevice(userId, currentDeviceId);
      
        
        if (saveResult) {
          
          showNotification('success', 'Your new device has been registered successfully.');
          return true;
        } else {
          
          throw new Error('Failed to register your new device. Please try again.');
        }
        
      } else if (userDevice.deviceId === currentDeviceId) {
        
        
        return true;
        
      } else {
       
        
        throw new Error('Access denied! You can only login from your registered device. Please contact your administrator to reset your device registration.');
      }
      
    } catch (error) {
      
      throw error;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!isOnline) {
      setError("");
      showNotification('error', 'You are offline. Please connect to the internet to log in.');
      setIsLoading(false);
      return;
    }

    try {
      // Setup persistence first
      await setupAuthPersistence();

      // Step 1: Backend Authentication
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ employeeId, password }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.message);
        setIsLoading(false);
        return;
      }

      // Step 2: Firebase Authentication
      
      const customToken = data.token;
      const userCredential = await signInWithCustomToken(auth, customToken);
      const idToken = await userCredential.user.getIdTokenResult();

      const role = idToken.claims.role || "user";
      const companyId = data.companyId;

      // Step 3: Device Check (BEFORE storing tokens and navigating)
      if (role === 'user') {
      
        try {
          const deviceCheckResult = await handleDeviceCheck(userCredential.user.uid, role);
          
          if (!deviceCheckResult) {
           
            await auth.signOut();
            setError('Device verification failed. Please contact your administrator.');
            setIsLoading(false);
            return;
          }
        } catch (deviceError) {
          
          await auth.signOut();
          setError(deviceError.message);
          setIsLoading(false);
          return; // STOP HERE - Don't proceed with login
        }
      } else {
        
      }

      // Step 4: Only proceed if device check passed (or was skipped for admin)
    
      localStorage.setItem("idToken", idToken.token);
      localStorage.setItem("userRole", role);
      localStorage.setItem("companyId", companyId);

      showNotification('success', "Login successful!");
      
      // Step 5: Navigate after successful device check
      setTimeout(() => {
        setIsLoading(false);
        if (role === "Superadmin") {
          navigate("/superadmin/dashboard");
        } else if (role === "admin") {
          navigate("/dashboard"); 
        } else if (role === "user") {
          navigate("/user/dashboard");
        } else {
          showNotification('error', "Unauthorized role. Please contact the administrator.");
          navigate("/");
        }
      }, 2000);
      
    } catch (err) {
      console.error("Login Error:", err);
      if (isOnline) {
        setError("An error occurred during login.");
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="loginPage flex"> 
      {!isOnline && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          background: "#ffcccc",
          color: "#900",
          textAlign: "center",
          padding: "10px",
          zIndex: 9999
        }}>
          You are offline. Some features may not work.
        </div>
      )}
      <Toast 
            type={notification.type} 
            message={notification.message} 
            onClose={() => setNotification({ type: '', message: '' })} 
          />
      <div className="container flex">
        <div className="formDiv flex">
          <div className="headerDiv">
            <img src={logo} alt="loginImg" />
            <h3>Attendance App</h3>
          </div>

        

          <form onSubmit={handleLogin} className="form grid">
            {error && <span className="showMessage">{error}</span>}

            <div className="inputDiv">
              <label htmlFor="employeeId">Employee ID</label>
              <div className="input flex">
                <FaUserShield className="icon" />
                <input 
                  type="text" 
                  id="employeeId" 
                  placeholder="Enter Employee ID" 
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="inputDiv">
              <label htmlFor="password">Password</label>
              <div className="input flex">
                <BsFillShieldLockFill className="icon" />
                <input 
                  type="password" 
                  id="password" 
                  placeholder="Enter Password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="btn" disabled={isLoading}>
              <span>{isLoading ? "Logging in..." : "Login"}</span>
              {!isLoading && <AiOutlineSwapRight className="icon" />}
            </button>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <p style={{ fontSize: '14px', color: 'var(--greytext)', margin: '0 0 8px 0' }}>
                Don't have an account?
              </p>
              <button 
                type="button" 
                onClick={() => navigate('/create-account')}
                style={{
                  background: 'none',
                  border: '2px solid var(--primarycolor)',
                  color: 'var(--primarycolor)',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--primarycolor)';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = 'var(--primarycolor)';
                }}
              >
                Create Account Request
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
